import React from "react";
import styled from "styled-components";
import FXModule from "./FXModule";
import { FXType } from "./FXDrawer";

interface OtherFXControlsProps {
  onFXSelect: (fx: FXType) => void;
  reverbMix: number;
  delayMix: number;
  reverseMix: number;
  onReverbMixChange: (value: number) => void;
  onDelayMixChange: (value: number) => void;
  onReverseMixChange: (value: number) => void;
  mappingMode?: string | null;
  fxModes?: {
    filter: boolean;
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  };
  onMappingModeChange?: (mode: string | null) => void;
  onFxModeChange?: (modes: {
    filter: boolean;
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  }) => void;
  onInitializeStepsWithGlobalValue?: (
    fxType: string,
    globalValue: number
  ) => void;
  hasMappingApplied?: (fxType: string) => boolean;
}

const OtherFXControls: React.FC<OtherFXControlsProps> = ({
  onFXSelect,
  reverbMix,
  delayMix,
  reverseMix,
  onReverbMixChange,
  onDelayMixChange,
  onReverseMixChange,
  mappingMode,
  fxModes,
  onMappingModeChange,
  onFxModeChange,
  onInitializeStepsWithGlobalValue,
  hasMappingApplied,
}) => {
  const createGraphClickHandler = (
    fxType: "reverb" | "delay" | "reverse",
    currentMix: number
  ) => {
    return () => {
      if (onMappingModeChange) {
        // Toggle mapping mode: if already mapping this FX, exit; otherwise enter mapping
        if (mappingMode === fxType) {
          onMappingModeChange(null);
        } else {
          onMappingModeChange(fxType);
          // Switch to per-step mode if not already
          const wasGlobalMode = fxModes && !fxModes[fxType];
          if (onFxModeChange && fxModes) {
            onFxModeChange({ ...fxModes, [fxType]: true });
          }
          // Initialize steps with current global value when switching to per-step mode
          if (wasGlobalMode && onInitializeStepsWithGlobalValue) {
            onInitializeStepsWithGlobalValue(fxType, currentMix);
          }
        }
      }
    };
  };

  return (
    <OtherFxContainer>
      <FXModule
        name="Reverse"
        mixValue={reverseMix}
        onMixChange={onReverseMixChange}
        color="#E91E63"
        onDrawerOpen={() => onFXSelect("reverse")}
        onGraphClick={createGraphClickHandler("reverse", reverseMix)}
        isPerStepMode={fxModes?.reverse || false}
        isMappingActive={mappingMode === "reverse"}
        hasMappingApplied={
          hasMappingApplied ? hasMappingApplied("reverse") : false
        }
      />

      <FXModule
        name="Reverb"
        mixValue={reverbMix}
        onMixChange={onReverbMixChange}
        color="#2196F3"
        onDrawerOpen={() => onFXSelect("reverb")}
        onGraphClick={createGraphClickHandler("reverb", reverbMix)}
        isPerStepMode={fxModes?.reverb || false}
        isMappingActive={mappingMode === "reverb"}
        hasMappingApplied={
          hasMappingApplied ? hasMappingApplied("reverb") : false
        }
      />

      <FXModule
        name="Delay"
        mixValue={delayMix}
        onMixChange={onDelayMixChange}
        color="#9C27B0"
        onDrawerOpen={() => onFXSelect("delay")}
        onGraphClick={createGraphClickHandler("delay", delayMix)}
        isPerStepMode={fxModes?.delay || false}
        isMappingActive={mappingMode === "delay"}
        hasMappingApplied={
          hasMappingApplied ? hasMappingApplied("delay") : false
        }
      />
    </OtherFxContainer>
  );
};

export default OtherFXControls;

const OtherFxContainer = styled.div.attrs({
  id: "other-fx-controls",
})`
  display: flex;
  gap: 12px;
  min-height: 100%;
  overflow-x: auto;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;
